import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import RegisterApi from "../../../Services/Register";
import DriverApi from "../../../Services/Driver";
import Header from "../../../components/header";
import Footer from "../../../components/footer";
import { showSnackbar } from "Redux/SnackBar/actions";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import "./signUpBusiness.css";

const stepLabels = [
  { number: 1, lines: ["signupBusiness.step.label1.line1", "signupBusiness.step.label1.line2"] },
  { number: 2, lines: ["signupBusiness.step.label2.line1", "signupBusiness.step.label2.line2"] },
  { number: 3, lines: ["signupBusiness.step.label3.line1", "signupBusiness.step.label3.line2"] },
  { number: 4, lines: ["signupBusiness.step.label4.line1", "signupBusiness.step.label4.line2"] },
];

const stepIntroEditorialContent = {
  1: {
    eyebrowLines: ["signupBusiness.intro.eyebrow.step1", "signupBusiness.intro.eyebrowSub.step1"],
    items: [
      {
        title: "signupBusiness.intro.item1.title.step1",
        text: "signupBusiness.intro.item1.text.step1",
        icon: UserIcon,
      },
      {
        title: "signupBusiness.intro.item2.title.step1",
        text: "signupBusiness.intro.item2.text.step1",
        icon: ShieldFeatureIcon,
      },
      {
        title: "signupBusiness.intro.item3.title.step1",
        text: "signupBusiness.intro.item3.text.step1",
        icon: ArrowRightIcon,
      },
    ],
  },
  2: {
    eyebrowLines: ["signupBusiness.intro.eyebrow.step2", "signupBusiness.intro.eyebrowSub.step2"],
    items: [
      {
        title: "signupBusiness.intro.item1.title.step2",
        text: "signupBusiness.intro.item1.text.step2",
        icon: UserIcon,
      },
      {
        title: "signupBusiness.intro.item2.title.step2",
        text: "signupBusiness.intro.item2.text.step2",
        icon: ShieldFeatureIcon,
      },
      {
        title: "signupBusiness.intro.item3.title.step2",
        text: "signupBusiness.intro.item3.text.step2",
        icon: ArrowRightIcon,
      },
    ],
  },
  3: {
    eyebrowLines: ["signupBusiness.intro.eyebrow.step3", "signupBusiness.intro.eyebrowSub.step3"],
    items: [
      {
        title: "signupBusiness.intro.item1.title.step3",
        text: "signupBusiness.intro.item1.text.step3",
        icon: ShieldFeatureIcon,
      },
      {
        title: "signupBusiness.intro.item2.title.step3",
        text: "signupBusiness.intro.item2.text.step3",
        icon: CheckIcon,
      },
      {
        title: "signupBusiness.intro.item3.title.step3",
        text: "signupBusiness.intro.item3.text.step3",
        icon: LockFeatureIcon,
      },
    ],
  },
  4: {
    eyebrowLines: ["signupBusiness.intro.eyebrow.step4", "signupBusiness.intro.eyebrowSub.step4"],
    items: [
      {
        title: "signupBusiness.intro.item1.title.step4",
        text: "signupBusiness.intro.item1.text.step4",
        icon: ShieldFeatureIcon,
      },
      {
        title: "signupBusiness.intro.item2.title.step4",
        text: "signupBusiness.intro.item2.text.step4",
        icon: DocumentFeatureIcon,
      },
      {
        title: "signupBusiness.intro.item3.title.step4",
        text: "signupBusiness.intro.item3.text.step4",
        icon: ClockFeatureIcon,
      },
    ],
  },
};

const introTitleLines = ["signupBusiness.intro.title.line1", "signupBusiness.intro.title.line2", "signupBusiness.intro.title.line3"];
const introSummary = "signupBusiness.intro.summary";

const countryOptions = [
  { value: "CM", label: "signupBusiness.country.cm" },
  { value: "CI", label: "signupBusiness.country.ci" },
  { value: "SN", label: "signupBusiness.country.sn" },
  { value: "GA", label: "signupBusiness.country.ga" },
];

const sectorOptions = [
  { value: "Logistique", label: "signupBusiness.sector.logistics" },
  { value: "Commerce", label: "signupBusiness.sector.commerce" },
  { value: "Industrie", label: "signupBusiness.sector.industry" },
  { value: "Transport", label: "signupBusiness.sector.transport" },
  { value: "Technologie", label: "signupBusiness.sector.technology" },
  { value: "Agriculture", label: "signupBusiness.sector.agriculture" },
  { value: "Services", label: "signupBusiness.sector.services" },
  { value: "Santé", label: "signupBusiness.sector.health" },
  { value: "Éducation", label: "signupBusiness.sector.education" },
  { value: "Autre", label: "signupBusiness.sector.other" },
];

const documentDescriptionByType = {
  "Immatriculation de l'entreprise (RCCM)": "signupBusiness.docDesc.rccm",
  "Attestation de conformité fiscale": "signupBusiness.docDesc.tax",
  "Pièce d'identité du représentant légal": "signupBusiness.docDesc.id",
};

const defaultDocumentDescription = "signupBusiness.docDesc.default";
const documentUploadHint = "signupBusiness.document.hint";
const maxFileSize = 10 * 1024 * 1024;

function getStepStatus(currentStep, stepNumber) {
  if (currentStep > stepNumber) {
    return "complete";
  }

  if (currentStep === stepNumber) {
    return "active";
  }

  return "upcoming";
}

function appendIfPresent(formData, key, value) {
  if (value === undefined || value === null || value === "") {
    return;
  }

  formData.append(key, value);
}

function splitFullName(fullName) {
  const trimmed = (fullName || "").trim();
  const spaceIndex = trimmed.indexOf(" ");

  if (spaceIndex === -1) {
    return { firstName: trimmed, lastName: "" };
  }

  return {
    firstName: trimmed.slice(0, spaceIndex),
    lastName: trimmed.slice(spaceIndex + 1),
  };
}

function buildTemporaryPassword(phone) {
  const digits = (phone || "").replace(/\D/g, "").slice(-6) || "202607";
  return `Mtu@${digits}!`;
}

function getDisplayValue(value, t) {
  return value || t("signupBusiness.noData");
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M9.2 16.2 4.9 11.9l-1.4 1.4 5.7 5.7L20.5 7.7l-1.4-1.4z" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m10 4 8 8-8 8-1.4-1.4 5.6-5.6H4v-2h10.2l-5.6-5.6z" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M14 4 5.7 12 14 20l1.4-1.4-5.6-5.6H20v-2H9.8l5.6-5.6z" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 12.1a4.7 4.7 0 1 0-4.7-4.7 4.7 4.7 0 0 0 4.7 4.7zm0 2.2c-4.2 0-7.6 2.3-7.6 5.1V21h15.2v-1.6c0-2.8-3.4-5.1-7.6-5.1z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M7.8 3.8a1.5 1.5 0 0 1 2.1.2l2 2.6a1.5 1.5 0 0 1-.2 2L10.1 10a11.2 11.2 0 0 0 4 4l1.4-1.6a1.5 1.5 0 0 1 2-.2l2.6 2a1.5 1.5 0 0 1 .2 2.1l-1.2 1.6a2.1 2.1 0 0 1-2.5.7c-5.8-2.1-10.3-6.6-12.4-12.4a2.1 2.1 0 0 1 .7-2.5z" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m12 15.4-6-6 1.4-1.4 4.6 4.6 4.6-4.6 1.4 1.4z" />
    </svg>
  );
}

function ShieldFeatureIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 2.5 4 5.7v5.1c0 5 3.2 8.9 8 10.7 4.8-1.8 8-5.7 8-10.7V5.7zM11 12.6l5.2-5.2 1.4 1.4L11 15.4 7.4 11.8l1.4-1.4z" />
    </svg>
  );
}

function LockFeatureIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9h14v-9a2 2 0 0 0-2-2zm-6 0V7a1 1 0 0 1 2 0v2h-2z" />
    </svg>
  );
}

function DocumentFeatureIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 2h9l5 5v15H6zm8 1.5V8h4.5zM8 12h8v1.6H8zm0 4h8v1.6H8z" />
    </svg>
  );
}

function ClockFeatureIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 10.4 3.2 2-1 1.6-4.2-2.6V6h2v6.4z" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="3" width="10" height="18" rx="1" />
      <rect x="14" y="9" width="6" height="12" rx="1" />
      <path d="M7 7h2M7 11h2M7 15h2M16 12h1M16 16h1" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7 3h7l4 4v14H7z" />
      <path d="M14 3v4h4" />
      <path d="M9.5 13h5M9.5 16.5h5" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 16V4" />
      <path d="m7 8 5-5 5 5" />
      <path d="M5 16v3a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function Stepper({ currentStep }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const progress = Math.min(
    Math.max(((currentStep - 0.5) / stepLabels.length) * 100, 12.5),
    87.5,
  );

  return (
    <div className="business-signup-stepper" style={{ "--step-progress": `${progress}%` }}>
      {stepLabels.map((step) => {
        const status = getStepStatus(currentStep, step.number);

        return (
          <div key={step.number} className={`business-signup-step is-${status}`}>
            <span className="business-signup-step__dot" aria-hidden="true">
              {status === "complete" ? <CheckIcon /> : step.number}
            </span>
            <div className="business-signup-step__label">
              {step.lines.map((line) => (
                <span key={line}>{t(line)}</span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FeatureList({ items }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  return (
    <div className="business-signup-features">
      {items.map((feature) => {
        const Icon = feature.icon;

        return (
          <div className="business-signup-feature" key={feature.title}>
            <span className="business-signup-feature__icon" aria-hidden="true">
              <Icon />
            </span>
            <div>
              <h3>{t(feature.title)}</h3>
              <p>{t(feature.text)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StepIntroPanel({ currentStep }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const content = stepIntroEditorialContent[currentStep] || stepIntroEditorialContent[4];

  return (
    <div className="business-signup-intro">
      <div className="business-signup-intro__eyebrow">
        {content.eyebrowLines.map((line) => (
          <span key={line}>{t(line)}</span>
        ))}
      </div>
      <h2 className="business-signup-intro__title">
        {introTitleLines.map((line, index) => {
          const isAccent = index === introTitleLines.length - 1;

          return (
            <span key={line} className={isAccent ? "is-accent" : ""}>
              {t(line)}
            </span>
          );
        })}
      </h2>
      <p className="business-signup-intro__summary">{t(introSummary)}</p>
      <div className="business-signup-intro__divider" aria-hidden="true" />
      <FeatureList items={content.items} />
    </div>
  );
}

function RequiredLabel({ children }) {
  return (
    <span className="business-signup-required-label">
      {children}
      <span aria-hidden="true" className="business-signup-required-label__asterisk">
        *
      </span>
    </span>
  );
}

function LabeledInput({
  label,
  required,
  value,
  onChange,
  placeholder,
  type = "text",
  name,
  error,
  autoComplete,
  inputMode,
  as = "input",
  rows = 3,
  className = "",
}) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  return (
    <label className={`business-signup-control ${className}`.trim()}>
      <span className="business-signup-control__label">
        {required ? <RequiredLabel>{t(label)}</RequiredLabel> : t(label)}
      </span>
      <div
        className={`business-signup-field business-signup-field--labelled${error ? " is-error" : ""}`.trim()}
      >
        {as === "textarea" ? (
          <textarea
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            rows={rows}
            aria-invalid={error ? "true" : "false"}
          />
        ) : (
          <input
            name={name}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            autoComplete={autoComplete}
            inputMode={inputMode}
            aria-invalid={error ? "true" : "false"}
          />
        )}
      </div>
      {error ? <p className="business-signup-error">{t(error)}</p> : null}
    </label>
  );
}

function SelectField({
  label,
  required,
  value,
  onChange,
  name,
  error,
  children,
  className = "",
}) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  return (
    <label className={`business-signup-control ${className}`.trim()}>
      <span className="business-signup-control__label">
        {required ? <RequiredLabel>{t(label)}</RequiredLabel> : t(label)}
      </span>
      <div
        className={`business-signup-field business-signup-field--labelled business-signup-field--select${error ? " is-error" : ""}`.trim()}
      >
        <select
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          aria-invalid={error ? "true" : "false"}
        >
          {children}
        </select>
        <span className="business-signup-field__trailing" aria-hidden="true">
          <ChevronDownIcon />
        </span>
      </div>
      {error ? <p className="business-signup-error">{t(error)}</p> : null}
    </label>
  );
}

function PhoneField({ value, onChange, error, placeholder, name }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  return (
    <label className="business-signup-control">
      <span className="business-signup-control__label">
        <RequiredLabel>{t("signupBusiness.label.contactPhone")}</RequiredLabel>
      </span>
      <div className={`business-signup-phone${error ? " is-error" : ""}`}>
        <span className="business-signup-phone__icon" aria-hidden="true">
          <PhoneIcon />
        </span>
        <span className="business-signup-phone__code">+237</span>
        <input
          name={name}
          type="tel"
          inputMode="tel"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          aria-invalid={error ? "true" : "false"}
        />
      </div>
      {error ? <p className="business-signup-error">{t(error)}</p> : null}
    </label>
  );
}

function DocumentUploadCard({ label, file, onChange, error, name }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return undefined;
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(nextPreviewUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewUrl);
    };
  }, [file]);

  const descKey = documentDescriptionByType[label] || defaultDocumentDescription;
  const description = t(descKey);

  return (
    <div className={`business-signup-doc-card${error ? " is-error" : ""}`}>
      <span className="business-signup-doc-card__icon" aria-hidden="true">
        <FileIcon />
      </span>
      <div className="business-signup-doc-card__body">
        <span className="business-signup-doc-card__title">{label}</span>
        <span className="business-signup-doc-card__text">{description}</span>
        {file ? (
          <span className="business-signup-doc-card__file">
            {file.name}
            {previewUrl ? (
              <button
                type="button"
                className="business-signup-doc-preview"
                onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}
                aria-label={t("signupBusiness.upload.preview")}
              >
                <EyeIcon />
              </button>
            ) : null}
          </span>
        ) : null}
        {error ? (
          <p className="business-signup-error">
            {error === "signupBusiness.error.docRequired"
              ? `${label} ${t(error)}`
              : t(error)}
          </p>
        ) : null}
      </div>
      <div className="business-signup-doc-card__action">
        <label className="business-signup-doc-upload">
          <input type="file" name={name} accept=".jpg,.jpeg,.png,.pdf" onChange={onChange} />
          <UploadIcon />
          {t("signupBusiness.upload.download")}
        </label>
      </div>
    </div>
  );
}

function VerifyItem({ label, value }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  return (
    <div className="business-signup-verify-item">
      <span className="business-signup-verify-item__label">{t(label)}</span>
      <span className="business-signup-verify-item__value">{getDisplayValue(value, t)}</span>
    </div>
  );
}

function VerifyCard({ icon: Icon, title, onEdit, children }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  return (
    <div className="business-signup-verify-card">
      <div className="business-signup-verify-card__header">
        <span className="business-signup-verify-card__title">
          <span className="business-signup-verify-card__icon" aria-hidden="true">
            <Icon />
          </span>
          {t(title)}
        </span>
        <button type="button" className="business-signup-verify-edit" onClick={onEdit}>
          <PencilIcon />
          {t("signupBusiness.verify.edit")}
        </button>
      </div>
      {children}
    </div>
  );
}

function validateStepOne(form) {
  const nextErrors = {};

  if (!form.companyName.trim()) nextErrors.companyName = "signupBusiness.error.companyName";
  if (!form.rccmNumber.trim()) nextErrors.rccmNumber = "signupBusiness.error.rccmNumber";
  if (!form.taxId.trim()) nextErrors.taxId = "signupBusiness.error.taxId";
  if (!form.country.trim()) nextErrors.country = "signupBusiness.error.country";
  if (!form.sector.trim()) nextErrors.sector = "signupBusiness.error.sector";

  return nextErrors;
}

function validateStepTwo(form) {
  const nextErrors = {};

  if (!form.contactName.trim()) nextErrors.contactName = "signupBusiness.error.contactName";
  if (!form.contactEmail.trim()) nextErrors.contactEmail = "signupBusiness.error.contactEmail";
  if (!form.contactPhone.trim()) nextErrors.contactPhone = "signupBusiness.error.contactPhone";

  return nextErrors;
}

function validateStepThree(documentTypes, documentFiles) {
  const nextErrors = {};

  documentTypes.forEach((item) => {
    if (!documentFiles[item.id]) {
      nextErrors[item.id] = "signupBusiness.error.docRequired";
    }
  });

  return nextErrors;
}

function validateStepFour(form) {
  const nextErrors = {};

  if (!form.acceptDeclaration) {
    nextErrors.acceptDeclaration = "signupBusiness.error.acceptDeclaration";
  }

  return nextErrors;
}

export default function SignUpBusiness() {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState({
    companyName: "",
    tradeName: "",
    rccmNumber: "",
    taxId: "",
    country: "CM",
    sector: "",
    description: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    estimatedRides: "",
    acceptDeclaration: false,
  });
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentTypesLoading, setDocumentTypesLoading] = useState(false);
  const [documentFiles, setDocumentFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    let isMounted = true;
    setDocumentTypesLoading(true);

    DriverApi.getDocumentsTypesV2("CORPORATE")
      .then((res) => {
        if (!isMounted) return;
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        setDocumentTypes(list);
      })
      .catch(() => {
        if (isMounted) setDocumentTypes([]);
      })
      .finally(() => {
        if (isMounted) setDocumentTypesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  function updateField(field) {
    return (event) => {
      const value = event.target.value;
      setForm((current) => ({
        ...current,
        [field]: value,
      }));
    };
  }

  function handleDocumentFileChange(documentTypeId) {
    return (event) => {
      const file = event.target.files?.[0];

      if (!file) {
        return;
      }

      if (file.size > maxFileSize) {
        setUploadErrors((current) => ({
          ...current,
          [documentTypeId]: "signupBusiness.error.fileSize",
        }));
        return;
      }

      setUploadErrors((current) => ({
        ...current,
        [documentTypeId]: "",
      }));

      setDocumentFiles((current) => ({
        ...current,
        [documentTypeId]: file,
      }));
    };
  }

  function goToStep(step) {
    setErrors({});
    setCurrentStep(step);
  }

  function goNext() {
    let nextErrors = {};

    if (currentStep === 1) {
      nextErrors = validateStepOne(form);
    } else if (currentStep === 2) {
      nextErrors = validateStepTwo(form);
    } else if (currentStep === 3) {
      nextErrors = validateStepThree(documentTypes, documentFiles);
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setCurrentStep((current) => Math.min(current + 1, 4));
  }

  function goBack() {
    setErrors({});
    setCurrentStep((current) => Math.max(current - 1, 1));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = validateStepFour(form);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const applicationPayload = new FormData();
    const { firstName, lastName } = splitFullName(form.contactName);
    const hiddenPassword = buildTemporaryPassword(form.contactPhone);

    appendIfPresent(applicationPayload, "firstName", firstName);
    appendIfPresent(applicationPayload, "lastName", firstName);
    appendIfPresent(applicationPayload, "phone", form.contactPhone.trim());
    appendIfPresent(applicationPayload, "email", form.contactEmail.trim());
    appendIfPresent(applicationPayload, "password", hiddenPassword);
    appendIfPresent(applicationPayload, "userType", "CORPORATE");
    appendIfPresent(applicationPayload, "deviceType", "WEB");
    appendIfPresent(applicationPayload, "country", form.country.trim());
    appendIfPresent(applicationPayload, "address", form.country.trim());

    appendIfPresent(applicationPayload, "primary_contact_name", form.contactName.trim());
    appendIfPresent(applicationPayload, "primary_contact_email", form.contactEmail.trim());
    appendIfPresent(applicationPayload, "primary_contact_phone", form.contactPhone.trim());
    appendIfPresent(applicationPayload, "primary_contact_course", form.estimatedRides.trim());

    appendIfPresent(applicationPayload, "business_name", form.companyName.trim());
    appendIfPresent(applicationPayload, "registration_number", form.rccmNumber.trim());
    appendIfPresent(applicationPayload, "tax_id", form.taxId.trim());
    appendIfPresent(applicationPayload, "industry_sector", form.sector.trim());
    appendIfPresent(applicationPayload, "description", form.description.trim());

    documentTypes
      .map((item) => ({ item, file: documentFiles[item.id] }))
      .filter((entry) => Boolean(entry.file))
      .forEach((entry, index) => {
        applicationPayload.append(`documents[${index}][image]`, entry.file);
        applicationPayload.append(`documents[${index}][document_type_id]`, entry.item.id);
        applicationPayload.append(`documents[${index}][driver_id]`, "");
      });

    dispatch(showLoader(t("signupBusiness.loader.submitting")));

    try {
      await RegisterApi.registerEnterprise(applicationPayload);
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: t("signupBusiness.success.message"),
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "success",
        }),
      );
      navigate("/login");
    } catch (error) {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            t("signupBusiness.error.submitFailed"),
          autoHideDuration: 4000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        }),
      );
    }
  }

  const submitLabel = currentStep === 4 ? t("signupBusiness.btn.submit") : t("signupBusiness.btn.next");

  return (
    <div className="business-signup-page">
      <a className="business-signup-skip" href="#main-content">
        {t("signupBusiness.skipToMain")}
      </a>

      <Header />

      <main className="business-signup-page__main" id="main-content">
        <div className="business-signup-shell">
          <section className="business-signup-card" aria-label="Inscription entreprise">
            <aside className="business-signup-card__intro">
              <StepIntroPanel currentStep={currentStep} />
            </aside>

            <section className="business-signup-card__form">
              <div className="business-signup-form__stepper">
                <Stepper currentStep={currentStep} />
              </div>

              <form className="business-signup-form" onSubmit={handleSubmit}>
                {currentStep === 1 ? (
                  <div className="business-signup-form__section">
                    <h3 className="business-signup-section__title">{t("signupBusiness.section.title.companyDetails")}</h3>
                    <p className="business-signup-section__subtitle">
                      {t("signupBusiness.section.subtitle.companyDetails")}
                    </p>

                    <div className="business-signup-grid business-signup-grid--2">
                      <LabeledInput
                        label="signupBusiness.label.companyName"
                        required
                        name="companyName"
                        value={form.companyName}
                        onChange={updateField("companyName")}
                        placeholder={t("signupBusiness.placeholder.companyName")}
                        error={errors.companyName}
                      />
                      <LabeledInput
                        label="signupBusiness.label.tradeName"
                        name="tradeName"
                        value={form.tradeName}
                        onChange={updateField("tradeName")}
                        placeholder={t("signupBusiness.placeholder.tradeName")}
                      />
                    </div>

                    <div className="business-signup-grid business-signup-grid--2">
                      <LabeledInput
                        label="signupBusiness.label.rccm"
                        required
                        name="rccmNumber"
                        value={form.rccmNumber}
                        onChange={updateField("rccmNumber")}
                        placeholder={t("signupBusiness.placeholder.rccm")}
                        error={errors.rccmNumber}
                      />
                      <LabeledInput
                        label="signupBusiness.label.taxId"
                        required
                        name="taxId"
                        value={form.taxId}
                        onChange={updateField("taxId")}
                        placeholder={t("signupBusiness.placeholder.taxId")}
                        error={errors.taxId}
                      />
                    </div>

                    <div className="business-signup-grid business-signup-grid--2">
                      <SelectField
                        label="signupBusiness.label.country"
                        required
                        name="country"
                        value={form.country}
                        onChange={updateField("country")}
                        error={errors.country}
                      >
                        {countryOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.label)}
                          </option>
                        ))}
                      </SelectField>

                      <SelectField
                        label="signupBusiness.label.sector"
                        required
                        name="sector"
                        value={form.sector}
                        onChange={updateField("sector")}
                        error={errors.sector}
                      >
                        <option value="" disabled>
                          {t("signupBusiness.placeholder.sector")}
                        </option>
                        {sectorOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.label)}
                          </option>
                        ))}
                      </SelectField>
                    </div>

                    <LabeledInput
                      label="signupBusiness.label.description"
                      name="description"
                      value={form.description}
                      onChange={updateField("description")}
                      placeholder={t("signupBusiness.placeholder.description")}
                      as="textarea"
                      rows={4}
                    />
                  </div>
                ) : null}

                {currentStep === 2 ? (
                  <div className="business-signup-form__section">
                    <h3 className="business-signup-section__title">{t("signupBusiness.section.title.contact")}</h3>
                    <p className="business-signup-section__subtitle">
                      {t("signupBusiness.section.subtitle.contact")}
                    </p>

                    <div className="business-signup-grid business-signup-grid--2">
                      <LabeledInput
                        label="signupBusiness.label.contactName"
                        required
                        name="contactName"
                        value={form.contactName}
                        onChange={updateField("contactName")}
                        placeholder={t("signupBusiness.placeholder.contactName")}
                        error={errors.contactName}
                        autoComplete="name"
                      />
                      <LabeledInput
                        label="signupBusiness.label.contactEmail"
                        required
                        name="contactEmail"
                        type="email"
                        value={form.contactEmail}
                        onChange={updateField("contactEmail")}
                        placeholder={t("signupBusiness.placeholder.contactEmail")}
                        error={errors.contactEmail}
                        autoComplete="email"
                      />
                    </div>

                    <div className="business-signup-grid business-signup-grid--2">
                      <PhoneField
                        name="contactPhone"
                        value={form.contactPhone}
                        onChange={updateField("contactPhone")}
                        placeholder={t("signupBusiness.placeholder.contactPhone")}
                        error={errors.contactPhone}
                      />
                      <LabeledInput
                        label="signupBusiness.label.estimatedRides"
                        name="estimatedRides"
                        type="number"
                        inputMode="numeric"
                        value={form.estimatedRides}
                        onChange={updateField("estimatedRides")}
                        placeholder={t("signupBusiness.placeholder.estimatedRides")}
                      />
                    </div>
                  </div>
                ) : null}

                {currentStep === 3 ? (
                  <div className="business-signup-form__section">
                    <h3 className="business-signup-section__title">{t("signupBusiness.section.title.documents")}</h3>
                    <p className="business-signup-section__subtitle">
                      {t("signupBusiness.section.subtitle.documents1")}
                    </p>
                    <p className="business-signup-section__subtitle">
                      {t("signupBusiness.section.subtitle.documents2")}
                    </p>

                    {documentTypesLoading ? (
                      <p className="business-signup-section__subtitle">
                        {t("signupBusiness.section.subtitle.loading")}
                      </p>
                    ) : null}

                    {!documentTypesLoading && documentTypes.length === 0 ? (
                      <p className="business-signup-error">
                        {t("signupBusiness.error.loadDocsFailed")}
                      </p>
                    ) : null}

                    {documentTypes.map((item) => (
                      <DocumentUploadCard
                        key={item.id}
                        label={item.documentType}
                        name={`document-${item.id}`}
                        file={documentFiles[item.id]}
                        onChange={handleDocumentFileChange(item.id)}
                        error={uploadErrors[item.id] || errors[item.id]}
                      />
                    ))}
                  </div>
                ) : null}

                {currentStep === 4 ? (
                  <div className="business-signup-form__section">
                    <h3 className="business-signup-section__title">{t("signupBusiness.section.title.verification")}</h3>
                    <p className="business-signup-section__subtitle">
                      {t("signupBusiness.section.subtitle.verification")}
                    </p>

                    <VerifyCard
                      icon={BuildingIcon}
                      title="signupBusiness.verify.companyDetails"
                      onEdit={() => goToStep(1)}
                    >
                      <div className="business-signup-verify-grid">
                        <VerifyItem label="signupBusiness.verify.legalName" value={form.companyName} />
                        <VerifyItem label="signupBusiness.verify.taxId" value={form.taxId} />
                        <VerifyItem label="signupBusiness.verify.tradeName" value={form.tradeName} />
                        <VerifyItem
                          label="signupBusiness.verify.country"
                          value={
                            countryOptions.find((option) => option.value === form.country)
                              ?.label || form.country
                          }
                        />
                        <VerifyItem label="signupBusiness.verify.rccm" value={form.rccmNumber} />
                        <VerifyItem label="signupBusiness.verify.sector" value={form.sector} />
                      </div>
                    </VerifyCard>

                    <VerifyCard
                      icon={UserIcon}
                      title="signupBusiness.verify.contact"
                      onEdit={() => goToStep(2)}
                    >
                      <div className="business-signup-verify-grid">
                        <VerifyItem label="signupBusiness.verify.fullName" value={form.contactName} />
                        <VerifyItem
                          label="signupBusiness.verify.phone"
                          value={form.contactPhone ? `+237 ${form.contactPhone}` : ""}
                        />
                        <VerifyItem label="signupBusiness.verify.email" value={form.contactEmail} />
                        <VerifyItem label="signupBusiness.verify.estimatedRides" value={form.estimatedRides} />
                      </div>
                    </VerifyCard>

                    <VerifyCard
                      icon={FileIcon}
                      title="signupBusiness.verify.documents"
                      onEdit={() => goToStep(3)}
                    >
                      <div className="business-signup-verify-docs">
                        {documentTypes.map((item) => {
                          const file = documentFiles[item.id];

                          return (
                            <div key={item.id} className="business-signup-verify-doc">
                              <span className="business-signup-verify-doc__label">
                                {item.documentType}
                              </span>
                              <span className="business-signup-verify-doc__value">
                                {file ? file.name : t("signupBusiness.verify.notProvided")}
                                {file ? (
                                  <button
                                    type="button"
                                    className="business-signup-doc-preview"
                                    onClick={() =>
                                      window.open(
                                        URL.createObjectURL(file),
                                        "_blank",
                                        "noopener,noreferrer",
                                      )
                                    }
                                    aria-label={t("signupBusiness.upload.preview")}
                                  >
                                    <EyeIcon />
                                  </button>
                                ) : null}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </VerifyCard>

                    <div className="business-signup-declaration">
                      <strong>{t("signupBusiness.declaration.title")}</strong>{" "}
                      {t("signupBusiness.declaration.text")}
                    </div>

                    <label className="business-signup-consent">
                      <input
                        type="checkbox"
                        checked={form.acceptDeclaration}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            acceptDeclaration: event.target.checked,
                          }))
                        }
                      />
                      <span className="business-signup-consent__box" aria-hidden="true">
                        <CheckIcon />
                      </span>
                      <span className="business-signup-consent__text">
                        {t("signupBusiness.consent.text", {
                          termsLink: (...chunks) => (
                            <Link to="/terms-conditions">{chunks}</Link>
                          ),
                        })}
                      </span>
                    </label>
                    {errors.acceptDeclaration ? (
                      <p className="business-signup-error">{t(errors.acceptDeclaration)}</p>
                    ) : null}
                  </div>
                ) : null}

                <div className="business-signup-actions">
                  {currentStep > 1 ? (
                    <button type="button" className="business-signup-back" onClick={goBack}>
                      <ArrowLeftIcon />
                      {t("signupBusiness.btn.back")}
                    </button>
                  ) : (
                    <span />
                  )}

                  {currentStep === 4 ? (
                    <button type="submit" className="business-signup-primary">
                      {submitLabel}
                      <ArrowRightIcon />
                    </button>
                  ) : (
                    <button type="button" className="business-signup-primary" onClick={goNext}>
                      {submitLabel}
                      <ArrowRightIcon />
                    </button>
                  )}
                </div>
              </form>
            </section>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
